import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { useAuthMe } from '../hooks/useAuth';

export function AuthGuard() {
  const { t } = useTranslation('common');
  const accessToken = useAuthStore((s) => s.accessToken);
  const isLoading = useAuthStore((s) => s.isLoading);
  const hydrateFromStorage = useAuthStore((s) => s.hydrateFromStorage);
  const location = useLocation();

  // Hydrate auth from localStorage on mount
  useEffect(() => {
    hydrateFromStorage();
  }, [hydrateFromStorage]);

  // Validate token against /auth/me
  const { isLoading: isMeLoading, isOnboarded } = useAuthMe();

  // Show loading spinner while checking auth
  if (isLoading || isMeLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
          <p className="text-sm text-surface-400">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // No token → redirect to login
  if (!accessToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Not onboarded → redirect to onboarding (unless already there)
  if (!isOnboarded && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Already onboarded → redirect away from onboarding
  if (isOnboarded && location.pathname === '/onboarding') {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}
